import * as Linking from 'expo-linking';
import * as Location from 'expo-location';
import { Alert, Platform } from 'react-native';
import type { LocationCoordinates } from './location';

/**
 * Emergency number database by country code (ISO 3166-1 alpha-2)
 * Common emergency numbers:
 * - 112: European emergency number (works in EU countries)
 * - 911: United States, Canada, parts of Latin America
 * - 999: United Kingdom, Ireland, Hong Kong, Macau
 * - 000: Australia
 * - 110: Police in many Asian countries
 * - 119: Fire/ambulance in many Asian countries
 */
const EMERGENCY_NUMBERS: Record<string, string> = {
  // European countries (112 is universal)
  AD: '112', // Andorra
  AT: '112', // Austria
  BE: '112', // Belgium
  BG: '112', // Bulgaria
  HR: '112', // Croatia
  CY: '112', // Cyprus
  CZ: '112', // Czech Republic
  DK: '112', // Denmark
  EE: '112', // Estonia
  FI: '112', // Finland
  FR: '112', // France
  DE: '112', // Germany
  GR: '112', // Greece
  HU: '112', // Hungary
  IE: '112', // Ireland
  IT: '112', // Italy
  LV: '112', // Latvia
  LT: '112', // Lithuania
  LU: '112', // Luxembourg
  MT: '112', // Malta
  NL: '112', // Netherlands
  PL: '112', // Poland
  PT: '112', // Portugal
  RO: '112', // Romania
  SK: '112', // Slovakia
  SI: '112', // Slovenia
  ES: '112', // Spain
  SE: '112', // Sweden
  GB: '999', // United Kingdom
  US: '911', // United States
  CA: '911', // Canada
  AU: '000', // Australia
  NZ: '111', // New Zealand
  JP: '110', // Japan (police)
  CN: '110', // China (police)
  KR: '119', // South Korea (fire/ambulance/rescue)
  TH: '191', // Thailand
  SG: '999', // Singapore (police emergency)
  MY: '999', // Malaysia
  ID: '112', // Indonesia
  VN: '115', // Vietnam (medical emergency)
  PH: '911', // Philippines
  HK: '999', // Hong Kong
  MO: '999', // Macau
  TW: '119', // Taiwan (fire/ambulance)
  IN: '112', // India
  BR: '190', // Brazil (police)
  MX: '911', // Mexico
  AR: '911', // Argentina
  CH: '112', // Switzerland
  NO: '112', // Norway
  IS: '112', // Iceland
  ZA: '10111', // South Africa
  EG: '122', // Egypt
  NG: '199', // Nigeria
  KE: '999', // Kenya
  AE: '999', // UAE
  SA: '999', // Saudi Arabia
  QA: '999', // Qatar
  OM: '999', // Oman
  JO: '911', // Jordan
  IL: '100', // Israel (police)
  TR: '112', // Turkey
  RU: '112', // Russia
};

/**
 * Get emergency number for a country code
 */
export const getEmergencyNumber = (countryCode?: string): string => {
  if (!countryCode) {
    // Default fallback
    return '112';
  }

  const code = countryCode.toUpperCase();
  return EMERGENCY_NUMBERS[code] || '112'; // Default to 112 (European standard, widely recognized)
};

/**
 * Get country code from coordinates using reverse geocoding
 * This is a simplified version - in production, you'd use a proper geocoding service
 */
export const getCountryCodeFromLocation = async (
  location: LocationCoordinates
): Promise<string | null> => {
  try {
    // Use reverse geocoding from expo-location to derive ISO country code.
    const results = await Location.reverseGeocodeAsync({
      latitude: location.latitude,
      longitude: location.longitude,
    });

    const countryCode = results?.[0]?.isoCountryCode || results?.[0]?.country;
    return countryCode ? countryCode.toUpperCase() : null;
  } catch (error) {
    console.error('[Emergency] Error getting country code from location:', error);
    return null;
  }
};

/**
 * Make an emergency call
 */
export const makeEmergencyCall = async (
  phoneNumber: string,
  showConfirmation: boolean = true
): Promise<void> => {
  try {
    if (showConfirmation) {
      Alert.alert(
        'Emergency Call',
        `Are you sure you want to call ${phoneNumber}?`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Call',
            style: 'default',
            onPress: async () => {
              await dialNumber(phoneNumber);
            },
          },
        ],
        { cancelable: true }
      );
    } else {
      await dialNumber(phoneNumber);
    }
  } catch (error) {
    console.error('[Emergency] Error making emergency call:', error);
    Alert.alert('Error', 'Unable to make emergency call. Please dial manually.');
  }
};

/**
 * Dial a phone number
 */
const dialNumber = async (phoneNumber: string): Promise<void> => {
  const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
  const telUrl = `tel:${cleanNumber}`;

  const canOpen = await Linking.canOpenURL(telUrl);
  if (canOpen) {
    await Linking.openURL(telUrl);
  } else {
    throw new Error('Cannot open phone dialer');
  }
};

/**
 * Get emergency number based on current location
 */
export const getEmergencyNumberForLocation = async (
  location: LocationCoordinates | null
): Promise<string> => {
  if (!location) {
    return getEmergencyNumber(); // Return default
  }

  const countryCode = await getCountryCodeFromLocation(location);
  return getEmergencyNumber(countryCode ?? undefined);
};





